// internals
import { buildQuoteName } from '@makemydeal/dr-dash-store/utils/quoteName';
import type { RecentDeal } from '../types/RecentDeals';
import { isQuoteDeal } from './isQuoteDeal';

export interface RecentDealNameContext {
    /** Dealer id (mmdId) the list is scoped to; used for the `Q-{dealerId}-...` form. */
    dealerId?: number | string;
    /** Quote Mode feature toggle (isManagerViewQuoteModeEnabled). */
    quoteNamingEnabled: boolean;
}

/**
 * Resolve the title shown for a Recent Deals / Deal List row.
 *
 * - Quote Mode off, or the record has a customer -> the existing customer name (unchanged behavior).
 * - Customer-less record (no `customerId`) with Quote Mode on -> the Quote Mode name:
 *     no DXG record (no `dealId`) -> 'Draft quote'
 *     otherwise                   -> `Q-{dealerId}-{YYYYMMDD-HHMMSS}` (UTC, from the deal's creation date)
 *
 * The naming rule itself is shared with the active-deal selector via buildQuoteName, so the list and
 * the open deal always format quote names identically.
 */
export const getRecentDealDisplayName = (
    deal: Pick<RecentDeal, 'customerName' | 'customerId' | 'dealId' | 'createdDate'>,
    { dealerId, quoteNamingEnabled }: RecentDealNameContext
): string => {
    if (!quoteNamingEnabled || !isQuoteDeal(deal)) {
        return deal.customerName;
    }

    return buildQuoteName({
        isDealXgRecordMissed: !deal.dealId,
        dealerId,
        creationDate: deal.createdDate
    });
};
